import { assetImageUrl, api, blobUrl } from "../api";
import { h, replace } from "../dom";
import { count, duration } from "../format";
import { icon } from "../icons";
import { store } from "../state";
import type { AssetCard, Face } from "../types";
import { failure, loading, toast } from "../ui";
import type { View } from "./view";

type Shape = "wide" | "tall" | "square";
type Motion = "ken_burns" | "pull_back" | "pan_left" | "pan_right" | "none";
type Transition = "crossfade" | "dissolve" | "cut";
type Mount = "none" | "blurred" | "plain" | "polaroid";
type CaptionAlignment = "left" | "center" | "right";

interface StudioSettings {
  shape: Shape;
  seconds: number;
  transitionSeconds: number;
  motion: Motion;
  transition: Transition;
  mount: Mount;
  useEdits: boolean;
  captions: boolean;
  captionFont: string;
  captionAlignment: CaptionAlignment;
  includeMotion: boolean;
  clipAudio: boolean;
  clipMaxSeconds: number;
  target: number;
}

const SHAPES: Record<Shape, { label: string; width: number; height: number }> = {
  wide: { label: "16:9 widescreen", width: 1920, height: 1080 },
  tall: { label: "Vertical", width: 1080, height: 1920 },
  square: { label: "Square", width: 1080, height: 1080 },
};

const FALLBACK_FACES: Face[] = [
  { id: "georgia", label: "Georgia", kind: "serif", css: "Georgia, 'Times New Roman', serif" },
  { id: "helvetica", label: "Helvetica", kind: "sans", css: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { id: "comicneue", label: "Comic Neue", kind: "hand", css: "'Comic Neue', 'Comic Sans MS', cursive" },
];

const STOP_WORDS = new Set([
  "a", "all", "and", "at", "for", "from", "in", "of", "on", "our", "photos",
  "photographs", "pictures", "reel", "show", "the", "their", "through", "with",
]);

function termsOf(prompt: string): string[] {
  return prompt
    .toLocaleLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((term) => term.length > 1 && !STOP_WORDS.has(term));
}

function searchable(asset: AssetCard): string {
  return [
    asset.caption,
    asset.description,
    asset.fileName,
    asset.capturedAt,
    ...asset.people.flatMap((person) => [person.name]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
}

function curate(assets: AssetCard[], prompt: string, target: number): AssetCard[] {
  const terms = termsOf(prompt);
  const dated = [...assets].sort((a, b) =>
    (a.capturedAt ?? "9999").localeCompare(b.capturedAt ?? "9999"),
  );
  if (!terms.length) return dated.slice(0, target);

  const scored = dated
    .map((asset, order) => {
      const text = searchable(asset);
      const score = terms.reduce((sum, term) => sum + (text.includes(term) ? 1 : 0), 0);
      return { asset, order, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .slice(0, target)
    .sort((a, b) => a.order - b.order)
    .map((item) => item.asset);
  return scored.length ? scored : dated.slice(0, target);
}

function sourceFor(asset: AssetCard, useEdits: boolean): string | null {
  return useEdits
    ? assetImageUrl(store.libraryId, asset)
    : blobUrl(asset.proxyKey ?? asset.contentHash);
}

function option(value: string, label: string): HTMLOptionElement {
  return h("option", { value, text: label });
}

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let assets: AssetCard[] = [];
  let faces: Face[] = FALLBACK_FACES;
  let staged: AssetCard[] = [];
  let frame = 0;
  let playing = true;
  let playhead = 0;
  let startedAt = performance.now();
  let resizeObserver: ResizeObserver | null = null;
  let destroyed = false;
  let nameTouched = false;
  let selectionApproved = false;
  let pickerOpen = false;
  let promptTimer: ReturnType<typeof setTimeout> | undefined;
  const images = new Map<string, HTMLImageElement>();
  const settings: StudioSettings = {
    shape: "wide",
    seconds: 5,
    transitionSeconds: 1,
    motion: "ken_burns",
    transition: "crossfade",
    mount: "polaroid",
    useEdits: true,
    captions: true,
    captionFont: "georgia",
    captionAlignment: "center",
    includeMotion: false,
    clipAudio: false,
    clipMaxSeconds: 6,
    target: 40,
  };

  const canvas = h("canvas", { "aria-label": "Animated reel preview" });
  const stage = h("div", { class: "reel-stage reel-stage-wide" }, canvas);
  const strip = h("div", { class: "reel-strip", "aria-label": "Staged photographs" });
  const picker = h("div", { class: "reel-photo-picker", hidden: true });
  const status = h("div", { class: "reel-studio-status mono", "aria-live": "polite" });
  const reviewStatus = h("span", { class: "reel-review-status mono" });
  const clock = h("span", { class: "reel-clock mono", text: "0:00 / 0:00" });
  const playButton = h("button", {
    class: "ib on",
    type: "button",
    title: "Pause preview",
    "aria-label": "Pause preview",
  });
  const renderButton = h("button", {
    class: "btn primary",
    type: "button",
    disabled: true,
    title: "Approve the curated photographs before rendering",
  }, icon("film", "i sm"), "Render reel") as HTMLButtonElement;
  const approveButton = h("button", {
    class: "btn",
    type: "button",
    text: "Approve selection",
  }) as HTMLButtonElement;
  const addButton = h("button", {
    class: "btn ghost",
    type: "button",
    text: "Add photos",
  }) as HTMLButtonElement;
  const prompt = h("textarea", {
    class: "reel-prompt",
    rows: "3",
    placeholder: "Nana across her life, with the family holidays and birthdays",
    "aria-label": "Curation prompt",
  }) as HTMLTextAreaElement;
  const name = h("input", {
    class: "full",
    type: "text",
    maxlength: "120",
    placeholder: "Untitled reel",
    "aria-label": "Reel name",
  }) as HTMLInputElement;
  const countRange = h("input", {
    type: "range",
    min: "4",
    max: "80",
    step: "1",
    value: String(settings.target),
  }) as HTMLInputElement;
  const countNow = h("span", { class: "now mono", text: String(settings.target) });
  const pace = h("input", {
    type: "range",
    min: "2",
    max: "10",
    step: ".5",
    value: String(settings.seconds),
  }) as HTMLInputElement;
  const paceNow = h("span", { class: "now mono", text: `${settings.seconds}s` });
  const editToggle = h("input", { type: "checkbox", checked: true }) as HTMLInputElement;
  const motionToggle = h("input", { type: "checkbox" }) as HTMLInputElement;
  const audioToggle = h("input", { type: "checkbox", disabled: true }) as HTMLInputElement;
  const clipLength = h("input", {
    type: "range",
    min: "2",
    max: "15",
    step: "1",
    value: String(settings.clipMaxSeconds),
    disabled: true,
  }) as HTMLInputElement;
  const clipLengthNow = h("span", { class: "now mono", text: `${settings.clipMaxSeconds}s` });
  const captionToggle = h("input", { type: "checkbox", checked: true }) as HTMLInputElement;
  const captionFont = h("select", { class: "full", "aria-label": "Caption font" }) as HTMLSelectElement;
  const motion = h(
    "select",
    { class: "full" },
    option("ken_burns", "Slow push"),
    option("pull_back", "Pull back"),
    option("pan_left", "Pan left"),
    option("pan_right", "Pan right"),
    option("none", "Still"),
  ) as HTMLSelectElement;
  const transition = h(
    "select",
    { class: "full" },
    option("crossfade", "Crossfade"),
    option("dissolve", "Dissolve"),
    option("cut", "Cut"),
  ) as HTMLSelectElement;

  const imageFor = (asset: AssetCard): HTMLImageElement | null => {
    const src = sourceFor(asset, settings.useEdits);
    if (!src) return null;
    const cached = images.get(src);
    if (cached) return cached;
    const image = new Image();
    image.decoding = "async";
    image.addEventListener("load", () => draw(performance.now()));
    image.src = src;
    images.set(src, image);
    return image;
  };

  const cover = (
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    scale = 1,
    shiftX = 0,
  ): void => {
    const ratio = Math.max(width / image.naturalWidth, height / image.naturalHeight) * scale;
    const sourceWidth = width / ratio;
    const sourceHeight = height / ratio;
    const sourceX = Math.max(0, (image.naturalWidth - sourceWidth) / 2 + shiftX * image.naturalWidth);
    const sourceY = Math.max(0, (image.naturalHeight - sourceHeight) / 2);
    context.drawImage(image, sourceX, sourceY, Math.min(sourceWidth, image.naturalWidth - sourceX), Math.min(sourceHeight, image.naturalHeight - sourceY), x, y, width, height);
  };

  const paintAsset = (
    context: CanvasRenderingContext2D,
    asset: AssetCard,
    progress: number,
    alpha: number,
  ): void => {
    const image = imageFor(asset);
    const width = canvas.width;
    const height = canvas.height;
    context.save();
    context.globalAlpha = alpha;
    context.fillStyle = "#0b0c0d";
    context.fillRect(0, 0, width, height);
    if (!image?.complete || !image.naturalWidth) {
      context.fillStyle = "#aaa49a";
      context.font = `${Math.max(18, width / 42)}px Georgia, serif`;
      context.textAlign = "center";
      context.fillText("Preparing photograph", width / 2, height / 2);
      context.restore();
      return;
    }

    context.save();
    context.filter = "blur(28px) brightness(.42)";
    cover(context, image, -24, -24, width + 48, height + 48, 1.08);
    context.restore();

    const travel = settings.motion === "none" ? 0 : .045 * progress;
    const scale = settings.motion === "pull_back" ? 1.07 - travel : 1.015 + travel;
    const direction = settings.motion === "pan_left" ? progress - .5 : settings.motion === "pan_right" ? .5 - progress : 0;
    const framed = settings.mount === "plain" || settings.mount === "polaroid";
    const fitted = settings.mount !== "none";
    const margin = framed ? Math.max(18, Math.min(width, height) * .055) : 0;
    const chin = settings.mount === "polaroid" ? margin * 1.9 : margin;
    const area = { x: margin, y: margin, width: width - margin * 2, height: height - margin - chin };
    if (framed) {
      context.fillStyle = "#f4efe5";
      context.fillRect(0, 0, width, height);
    }
    context.save();
    context.beginPath();
    context.rect(area.x, area.y, area.width, area.height);
    context.clip();
    if (fitted) {
      const ratio = Math.min(area.width / image.naturalWidth, area.height / image.naturalHeight) * scale;
      const drawWidth = image.naturalWidth * ratio;
      const drawHeight = image.naturalHeight * ratio;
      context.drawImage(
        image,
        area.x + (area.width - drawWidth) / 2 + direction * area.width * .05,
        area.y + (area.height - drawHeight) / 2,
        drawWidth,
        drawHeight,
      );
    } else {
      cover(context, image, area.x, area.y, area.width, area.height, scale, direction * .05);
    }
    context.restore();

    if (settings.mount === "polaroid" && settings.captions) {
      const label = asset.caption ?? asset.people.map((person) => person.name).filter(Boolean).join(", ");
      const year = asset.capturedAt?.slice(0, 4) ?? "";
      context.fillStyle = "#24211e";
      const face = faces.find((candidate) => candidate.id === settings.captionFont) ?? faces[0];
      context.textAlign = settings.captionAlignment;
      context.font = `${Math.max(15, Math.round(height * .034))}px ${face?.css ?? "Georgia, serif"}`;
      const yearSpace = year ? Math.max(72, width * .2) : 0;
      const captionLeft = margin * 1.15;
      const captionRight = width - margin * 1.15 - yearSpace;
      const captionX = settings.captionAlignment === "left"
        ? captionLeft
        : settings.captionAlignment === "right"
          ? captionRight
          : (captionLeft + captionRight) / 2;
      context.fillText(
        (label || asset.fileName).slice(0, 60),
        captionX,
        height - chin * .48,
        captionRight - captionLeft,
      );
      context.textAlign = "right";
      context.font = `${Math.max(12, Math.round(height * .025))}px ui-monospace, monospace`;
      context.fillText(year, width - margin * 1.15, height - chin * .48);
    }
    context.restore();
  };

  const draw = (now: number): void => {
    const context = canvas.getContext("2d");
    if (!context) return;
    if (playing && staged.length) playhead += Math.max(0, now - startedAt) / 1000;
    startedAt = now;
    const total = staged.length * settings.seconds;
    if (total && playhead >= total) playhead %= total;
    const index = total ? Math.floor(playhead / settings.seconds) % staged.length : 0;
    const within = total ? (playhead % settings.seconds) / settings.seconds : 0;
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (staged[index]) paintAsset(context, staged[index]!, within, 1);
    const fadeAt = 1 - Math.min(.9, settings.transitionSeconds / settings.seconds);
    if (settings.transition !== "cut" && within > fadeAt && staged.length > 1) {
      const next = staged[(index + 1) % staged.length]!;
      const mix = (within - fadeAt) / (1 - fadeAt);
      paintAsset(context, next, 0, settings.transition === "dissolve" ? mix * mix : mix);
    }
    clock.textContent = `${time(playhead)} / ${time(total)}`;
    for (const [thumbIndex, thumb] of [...strip.children].entries()) {
      thumb.classList.toggle("on", thumbIndex === index);
    }
  };

  const animate = (now: number): void => {
    if (destroyed) return;
    draw(now);
    frame = requestAnimationFrame(animate);
  };

  const resize = (): void => {
    const rect = stage.getBoundingClientRect();
    const ratio = Math.min(2, devicePixelRatio || 1);
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    draw(performance.now());
  };

  const describe = (asset: AssetCard): string =>
    asset.caption
    || asset.people.map((person) => person.name).filter(Boolean).join(", ")
    || asset.fileName;

  const selectionCount = (): string => count(
    staged.length,
    settings.includeMotion ? "item" : "photograph",
  );

  const motionBadge = (asset: AssetCard): HTMLElement | null => asset.kind === "video"
    ? h("span", { class: "reel-motion-badge", text: duration(asset.durationMs) || "Video" })
    : null;

  const setApproval = (approved: boolean): void => {
    selectionApproved = approved;
    renderButton.disabled = !approved || !staged.length;
    renderButton.title = approved ? "Render reel" : "Approve the curated photographs before rendering";
    approveButton.disabled = approved || !staged.length;
    approveButton.textContent = approved ? "Selection approved" : "Approve selection";
    reviewStatus.textContent = approved ? "Approved" : "Review needed";
    reviewStatus.classList.toggle("approved", approved);
  };

  const paintPicker = (): void => {
    const stagedIds = new Set(staged.map((asset) => asset.id));
    const available = assets.filter((asset) =>
      (settings.includeMotion || asset.kind === "image") && !stagedIds.has(asset.id));
    replace(
      picker,
      h(
        "div",
        { class: "reel-picker-head" },
        h("div", null,
          h("strong", { text: "Add from the library" }),
          h("small", { text: `${count(available.length, settings.includeMotion ? "item" : "photograph")} available` }),
        ),
        h("button", {
          class: "ib",
          type: "button",
          title: "Close photo browser",
          "aria-label": "Close photo browser",
          onclick: () => {
            pickerOpen = false;
            picker.hidden = true;
            addButton.setAttribute("aria-expanded", "false");
          },
        }, icon("x")),
      ),
      available.length
        ? h("div", { class: "reel-picker-grid" }, ...available.map((asset) => {
          const src = sourceFor(asset, settings.useEdits);
          return h(
            "button",
            {
              class: "reel-picker-photo",
              type: "button",
              title: `Add ${describe(asset)}`,
              onclick: () => {
                staged.push(asset);
                setApproval(false);
                status.textContent = `${selectionCount()} in your selection`;
                paintStrip();
                paintPicker();
                draw(performance.now());
              },
            },
            src ? h("img", { src, alt: "", loading: "lazy" }) : icon("image"),
            motionBadge(asset),
            h("span", { class: "reel-picker-caption", text: describe(asset) }),
          );
        }))
        : h("p", { class: "sub", text: "Every eligible item in this selection is already staged." }),
    );
  };

  const paintStrip = (): void => {
    replace(
      strip,
      ...staged.map((asset, index) => {
        const src = sourceFor(asset, settings.useEdits);
        return h(
          "div",
          { class: `reel-strip-photo${index === Math.floor(playhead / settings.seconds) ? " on" : ""}` },
          h(
            "button",
            {
              class: "reel-strip-preview",
              type: "button",
              title: `Preview ${describe(asset)}`,
              onclick: () => {
                playhead = index * settings.seconds;
                startedAt = performance.now();
                draw(startedAt);
              },
            },
            src ? h("img", { src, alt: describe(asset), loading: "lazy" }) : icon("image"),
            motionBadge(asset),
          ),
          h("button", {
            class: "reel-strip-remove",
            type: "button",
            title: `Remove ${describe(asset)}`,
            "aria-label": `Remove ${describe(asset)}`,
            onclick: () => {
              staged.splice(index, 1);
              playhead = Math.min(playhead, Math.max(0, staged.length * settings.seconds - settings.seconds));
              setApproval(false);
              status.textContent = `${selectionCount()} in your selection`;
              paintStrip();
              if (pickerOpen) paintPicker();
              draw(performance.now());
            },
          }, icon("x", "i sm")),
        );
      }),
    );
  };

  const restage = (): void => {
    const eligible = settings.includeMotion ? assets : assets.filter((asset) => asset.kind === "image");
    const terms = termsOf(prompt.value);
    const hasMatches = terms.length > 0 && eligible.some((asset) => {
      const text = searchable(asset);
      return terms.some((term) => text.includes(term));
    });
    staged = curate(eligible, prompt.value, settings.target);
    setApproval(false);
    playhead = 0;
    startedAt = performance.now();
    if (!nameTouched && prompt.value.trim()) name.value = prompt.value.trim().slice(0, 120);
    status.textContent = hasMatches
      ? `${selectionCount()} matching the prompt, staged chronologically`
      : terms.length
        ? `No exact metadata matches; ${selectionCount()} staged chronologically`
        : `${selectionCount()} staged in chronological order`;
    paintStrip();
      if (pickerOpen) paintPicker();
    draw(startedAt);
  };

  const setShape = (shape: Shape): void => {
    settings.shape = shape;
    stage.className = `reel-stage reel-stage-${shape}`;
    for (const button of pane.querySelectorAll<HTMLButtonElement>("[data-shape]")) {
      button.classList.toggle("on", button.dataset.shape === shape);
      button.setAttribute("aria-pressed", String(button.dataset.shape === shape));
    }
    resize();
  };

  const setMount = (mount: Mount): void => {
    settings.mount = mount;
    for (const button of pane.querySelectorAll<HTMLButtonElement>("[data-mount]")) {
      button.classList.toggle("on", button.dataset.mount === mount);
      button.setAttribute("aria-pressed", String(button.dataset.mount === mount));
    }
    captionToggle.disabled = mount !== "polaroid";
    captionFont.disabled = mount !== "polaroid";
    draw(performance.now());
  };

  const setCaptionAlignment = (alignment: CaptionAlignment): void => {
    settings.captionAlignment = alignment;
    for (const button of pane.querySelectorAll<HTMLButtonElement>("[data-caption-alignment]")) {
      button.classList.toggle("on", button.dataset.captionAlignment === alignment);
      button.setAttribute("aria-pressed", String(button.dataset.captionAlignment === alignment));
    }
    draw(performance.now());
  };

  const queueReel = async (): Promise<void> => {
    if (!staged.length) {
      toast("There are no photographs in this reel yet.", true);
      prompt.focus();
      return;
    }
    if (!selectionApproved) {
      toast("Review and approve the curated photographs before rendering.", true);
      approveButton.focus();
      return;
    }
    const reelName = name.value.trim();
    if (!reelName) {
      name.focus();
      return;
    }
    const shape = SHAPES[settings.shape];
    try {
      await api.makeReel(store.libraryId, reelName, staged.map((asset) => asset.id), {
        width: shape.width,
        height: shape.height,
        fps: 30,
        seconds_per_photo: settings.seconds,
        transition_seconds: settings.transitionSeconds,
        background: settings.mount === "none" ? "black" : "blurred_fill",
        photo_fit: settings.mount === "none" ? "cover" : "contain",
        motion: settings.motion,
        motion_intensity: .25,
        transition: settings.transition,
        bookend_fade: true,
        frame: settings.mount === "none" ? "none" : "medium",
        frame_style: settings.mount === "polaroid" ? "polaroid" : "plain",
        frame_caption: settings.mount === "polaroid" && settings.captions,
        caption_font: settings.captionFont,
        caption_alignment: settings.captionAlignment,
        use_motion_clips: settings.includeMotion,
        clip_audio: settings.includeMotion && settings.clipAudio,
        clip_max_seconds: settings.clipMaxSeconds,
        use_edits: settings.useEdits,
        curation_prompt: prompt.value.trim(),
      });
      toast("Building. It will appear on the Reels page when it is ready.");
      store.go("reels");
    } catch (error) {
      toast(error instanceof Error ? error.message : "could not start the reel", true);
    }
  };

  const segmented = <T extends string>(
    key: "shape" | "mount",
    choices: Array<[T, string]>,
    select: (value: T) => void,
  ): HTMLElement => h(
    "div",
    { class: "reel-segments" },
    ...choices.map(([value, label]) => {
      const selected = key === "shape" ? settings.shape === value : settings.mount === value;
      return h("button", {
      class: selected ? "on" : "",
      type: "button",
      "data-shape": key === "shape" ? value : undefined,
      "data-mount": key === "mount" ? value : undefined,
      "aria-pressed": String(selected),
      text: label,
      onclick: () => select(value),
    });}),
  );

  function studio(): HTMLElement {
    renderButton.addEventListener("click", () => void queueReel());
    approveButton.addEventListener("click", () => setApproval(true));
    addButton.setAttribute("aria-expanded", "false");
    addButton.addEventListener("click", () => {
      pickerOpen = !pickerOpen;
      picker.hidden = !pickerOpen;
      addButton.setAttribute("aria-expanded", String(pickerOpen));
      if (pickerOpen) paintPicker();
    });
    playButton.appendChild(icon("pause"));
    playButton.addEventListener("click", () => {
      playing = !playing;
      startedAt = performance.now();
      replace(playButton, icon(playing ? "pause" : "play"));
      playButton.classList.toggle("on", playing);
      playButton.title = playing ? "Pause preview" : "Play preview";
      playButton.setAttribute("aria-label", playButton.title);
    });
    prompt.addEventListener("input", () => {
      clearTimeout(promptTimer);
      promptTimer = setTimeout(restage, 220);
    });
    name.addEventListener("input", () => { nameTouched = true; });
    countRange.addEventListener("input", () => {
      settings.target = Number(countRange.value);
      countNow.textContent = countRange.value;
      restage();
    });
    pace.addEventListener("input", () => {
      settings.seconds = Number(pace.value);
      settings.transitionSeconds = Math.min(1, settings.seconds * .25);
      paceNow.textContent = `${settings.seconds}s`;
      playhead = 0;
    });
    editToggle.addEventListener("change", () => {
      settings.useEdits = editToggle.checked;
      images.clear();
      paintStrip();
      if (pickerOpen) paintPicker();
      draw(performance.now());
    });
    motionToggle.addEventListener("change", () => {
      settings.includeMotion = motionToggle.checked;
      audioToggle.disabled = !settings.includeMotion;
      clipLength.disabled = !settings.includeMotion;
      addButton.textContent = settings.includeMotion ? "Add photos or video" : "Add photos";
      restage();
    });
    audioToggle.addEventListener("change", () => {
      settings.clipAudio = audioToggle.checked;
    });
    clipLength.addEventListener("input", () => {
      settings.clipMaxSeconds = Number(clipLength.value);
      clipLengthNow.textContent = `${settings.clipMaxSeconds}s`;
    });
    captionToggle.addEventListener("change", () => {
      settings.captions = captionToggle.checked;
      draw(performance.now());
    });
    motion.addEventListener("change", () => { settings.motion = motion.value as Motion; });
    transition.addEventListener("change", () => { settings.transition = transition.value as Transition; });
    captionFont.addEventListener("change", () => {
      settings.captionFont = captionFont.value;
      draw(performance.now());
    });

    return h(
      "div",
      { class: "reel-studio" },
      h(
        "header",
        { class: "reel-studio-head" },
        h("button", { class: "btn ghost back", type: "button", onclick: () => store.go("reels") }, icon("chevron", "i sm"), "Reels"),
        h("div", null, h("h2", { text: "Make a reel" }), h("p", { class: "sub", text: "Stage the story, watch it move, then render only when it is ready." })),
        h("div", { class: "grow" }),
        renderButton,
      ),
      h(
        "div",
        { class: "reel-studio-grid" },
        h(
          "section",
          { class: "reel-preview-column" },
          stage,
          h(
            "div",
            { class: "reel-transport" },
            playButton,
            h("button", { class: "ib", type: "button", title: "Previous photograph", "aria-label": "Previous photograph", onclick: () => {
              playhead = Math.max(0, playhead - settings.seconds);
              startedAt = performance.now();
            } }, icon("skip-back")),
            h("button", { class: "ib", type: "button", title: "Next photograph", "aria-label": "Next photograph", onclick: () => {
              if (staged.length) playhead = (playhead + settings.seconds) % (staged.length * settings.seconds);
              startedAt = performance.now();
            } }, icon("skip-forward")),
            clock,
            h("div", { class: "grow" }),
            status,
          ),
          h(
            "div",
            { class: "reel-review-head" },
            h("div", null,
              h("strong", { text: "Curated photographs" }),
              reviewStatus,
            ),
            h("div", { class: "reel-review-actions" }, addButton, approveButton),
          ),
          strip,
          picker,
        ),
        h(
          "aside",
          { class: "reel-settings" },
          h("label", { class: "reel-field" }, h("span", { text: "Curation prompt" }), prompt),
          h("label", { class: "reel-field" }, h("span", { text: "Reel name" }), name),
          h("label", { class: "reel-field" }, h("span", null, "Photographs", countNow), countRange),
          h("div", { class: "reel-field" }, h("span", { text: "Shape" }), segmented("shape", [["wide", "16:9"], ["tall", "Vertical"], ["square", "Square"]], setShape)),
          h("label", { class: "reel-check" }, editToggle, h("span", null, h("strong", { text: "Apply archive edits" }), h("small", { text: "Use crops, repairs and colour work" }))),
          h("label", { class: "reel-field" }, h("span", null, "Pace", paceNow), pace),
          h("label", { class: "reel-field" }, h("span", { text: "Movement" }), motion),
          h("label", { class: "reel-field" }, h("span", { text: "Between photographs" }), transition),
          h("div", { class: "reel-field" }, h("span", { text: "Mount" }), segmented("mount", [["none", "Full"], ["blurred", "Blurred fit"], ["plain", "Border"], ["polaroid", "Polaroid"]], setMount)),
          h("label", { class: "reel-check" }, motionToggle, h("span", null, h("strong", { text: "Include video & Live Photos" }), h("small", { text: "Play motion clips in the finished reel" }))),
          h("label", { class: "reel-check" }, audioToggle, h("span", null, h("strong", { text: "Keep original audio" }), h("small", { text: "Include sound from motion clips" }))),
          h("label", { class: "reel-field" }, h("span", null, "Maximum clip length", clipLengthNow), clipLength),
          h("label", { class: "reel-check" }, captionToggle, h("span", null, h("strong", { text: "Write captions" }), h("small", { text: "On the Polaroid chin" }))),
          h("label", { class: "reel-field" }, h("span", { text: "Caption font" }), captionFont),
          h("div", { class: "reel-field" }, h("span", { text: "Caption alignment" }), h(
            "div",
            { class: "reel-segments" },
            ...(["left", "center", "right"] as CaptionAlignment[]).map((alignment) => h("button", {
              class: settings.captionAlignment === alignment ? "on" : "",
              type: "button",
              "data-caption-alignment": alignment,
              "aria-pressed": String(settings.captionAlignment === alignment),
              text: alignment[0]!.toUpperCase() + alignment.slice(1),
              onclick: () => setCaptionAlignment(alignment),
            })),
          )),
        ),
      ),
    );
  }

  async function refresh(): Promise<void> {
    const mine = ++generation;
    replace(pane, loading("reel studio"));
    try {
      const [result, fontResult] = await Promise.all([
        api.cards(store.libraryId, { ...store.filter, kind: undefined }, { limit: 400, order: "date" }),
        api.faces(store.libraryId).catch(() => ({ fonts: [] })),
      ]);
      if (mine !== generation) return;
      assets = result.items;
      faces = fontResult.fonts.length ? fontResult.fonts : FALLBACK_FACES;
      settings.captionFont = faces.some((face) => face.id === settings.captionFont)
        ? settings.captionFont
        : faces[0]!.id;
      replace(
        captionFont,
        ...faces.map((face) => option(face.id, `${face.label} · ${face.kind}`)),
      );
      captionFont.value = settings.captionFont;
      if (!assets.length) {
        replace(pane, failure(new Error("There are no photographs in this selection."), () => store.go("cards")));
        return;
      }
      replace(pane, studio());
      restage();
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(stage);
      resize();
      frame = requestAnimationFrame(animate);
    } catch (error) {
      if (mine === generation) replace(pane, failure(error, () => void refresh()));
    }
  }

  void refresh();
  return {
    refresh() {
      void refresh();
    },
    destroy() {
      destroyed = true;
      generation++;
      clearTimeout(promptTimer);
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
    },
  };
}

function time(seconds: number): string {
  if (!Number.isFinite(seconds)) return "0:00";
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}